import React, { useEffect, useRef } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { 
  Clock, 
  Users, 
  Zap, 
  BadgeDollarSign, 
  Star,
  Award
} from 'lucide-react';

gsap.registerPlugin(ScrollTrigger);

const features = [
  {
    id: 1,
    icon: Clock,
    title: '24/7 Availability',
    description: 'Round the clock service whenever you need us, including holidays and weekends.',
  },
  {
    id: 2,
    icon: Users,
    title: 'Expert Technicians',
    description: 'Certified professionals with years of experience in auto glass repair.',
  },
  {
    id: 3,
    icon: Zap,
    title: 'Fast Service',
    description: 'Quick turnaround time without compromising on quality and precision.',
  },
  {
    id: 4,
    icon: BadgeDollarSign,
    title: 'Affordable Pricing',
    description: 'Competitive rates with no hidden charges. Best wholesale prices available.',
  },
  {
    id: 5,
    icon: Star,
    title: '5 Star Rated',
    description: 'Trusted by hundreds of customers with consistently excellent reviews.',
  },
  {
    id: 6,
    icon: Award,
    title: 'AIS Authorized',
    description: 'Official Asahi India Glass dealer - India\'s leading integrated glass company.',
  },
];

const FeatureCard = ({ feature, index }) => {
  const Icon = feature.icon;
  
  return (
    <div 
      className="why-card"
      data-testid={`why-card-${feature.id}`}
    >
      {/* Animated icon container */}
      <div className="relative mb-6">
        <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-cyan-500/20 to-blue-500/10 flex items-center justify-center relative overflow-hidden group-hover:scale-110 transition-transform duration-500">
          {/* Rotating border */}
          <div className="absolute inset-0 rounded-2xl border border-cyan-500/30"></div>
          
          {/* Icon */}
          <Icon className="w-8 h-8 text-cyan-400 relative z-10" />
          
          {/* Pulse effect */}
          <div className="absolute inset-0 bg-cyan-500/20 rounded-2xl animate-ping opacity-0 group-hover:opacity-50"></div>
        </div>
        
        {/* Number indicator */}
        <div className="absolute -top-2 -right-2 w-8 h-8 bg-[#0A0A0A] border border-cyan-500/30 rounded-full flex items-center justify-center">
          <span className="text-cyan-400 font-orbitron text-sm font-bold">0{index + 1}</span>
        </div>
      </div>
      
      {/* Title */}
      <h3 className="font-orbitron text-xl font-bold text-white mb-3 uppercase tracking-wide">
        {feature.title}
      </h3>
      
      {/* Description */}
      <p className="text-gray-400 leading-relaxed">
        {feature.description}
      </p>
    </div>
  );
};

export default function WhyChooseSection() {
  const sectionRef = useRef(null);
  const titleRef = useRef(null);
  const cardsRef = useRef(null);

  useEffect(() => {
    const ctx = gsap.context(() => {
      // Title animation
      gsap.fromTo(
        titleRef.current,
        { opacity: 0, y: 50 },
        {
          opacity: 1,
          y: 0,
          duration: 1,
          ease: 'power3.out',
          scrollTrigger: {
            trigger: titleRef.current,
            start: 'top 80%',
            toggleActions: 'play none none reverse'
          }
        }
      );
      
      // Cards animation
      gsap.fromTo(
        '.why-card',
        { opacity: 0, y: 60, scale: 0.9 },
        {
          opacity: 1,
          y: 0,
          scale: 1,
          duration: 0.7,
          stagger: 0.12,
          ease: 'power3.out',
          scrollTrigger: {
            trigger: cardsRef.current,
            start: 'top 75%',
            toggleActions: 'play none none reverse'
          }
        }
      );
    }, sectionRef);

    return () => ctx.revert();
  }, []);

  return (
    <section 
      ref={sectionRef}
      id="why-choose" 
      className="relative py-20 lg:py-32 overflow-hidden"
      data-testid="why-choose-section"
    >
      {/* Background image overlay - Using actual shop image */}
      <div 
        className="absolute inset-0 bg-cover bg-center bg-no-repeat opacity-15"
        style={{ 
          backgroundImage: 'url(https://customer-assets.emergentagent.com/job_glass-repair-tech/artifacts/cswu4juj_WhatsApp%20Image%202026-02-19%20at%201.30.10%20PM%20%281%29.jpeg)'
        }}
      ></div>
      
      {/* Gradient overlay */}
      <div className="absolute inset-0 bg-gradient-to-b from-[#050505] via-transparent to-[#050505]"></div>
      
      <div className="container mx-auto px-4 relative z-10">
        {/* Section header */}
        <div ref={titleRef} className="text-center mb-16">
          <span className="inline-block text-cyan-400 text-sm font-semibold tracking-[0.3em] uppercase mb-4">
            Why Trust Us
          </span>
          <h2 className="font-orbitron text-3xl sm:text-4xl lg:text-5xl font-black text-white uppercase tracking-wider mb-6">
            Why <span className="gradient-text">Choose Us</span>
          </h2>
          <p className="text-gray-400 text-lg max-w-2xl mx-auto">
            Experience the difference with Dehradun's most trusted auto glass specialists
          </p>
        </div>
        
        {/* Features grid */}
        <div ref={cardsRef} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
          {features.map((feature, index) => (
            <FeatureCard key={feature.id} feature={feature} index={index} />
          ))}
        </div>
      </div>
    </section>
  );
}
